import PublicLayout from "@/components/PublicLayout";
import { ScrollText, HeartHandshake, Scale, Cross } from "lucide-react";

export default function Terms() {
  return (
    <PublicLayout>
      <section className="celestial-bg">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 py-20">
          <p className="text-xs uppercase tracking-widest text-brand-quiet">Terms & Conditions</p>
          <h1 className="mt-3 font-serif text-5xl sm:text-6xl tracking-tight text-brand-ink">
            A simple, honest agreement.
          </h1>
          <p className="mt-6 text-lg text-brand-quiet leading-relaxed max-w-2xl">
            By using Path to Repentance you agree to the terms below. They are written to protect
            you and to keep the space sacred for every believer who walks through it.
          </p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 lg:px-8 py-14 space-y-10">
        <Block icon={ScrollText} title="1. What Path to Repentance is.">
          Path to Repentance is a private spiritual companion: a sin tracker, a prayer journal, and an
          AI counselor that answers from the Bible. It is not a substitute for a pastor, priest,
          medical professional, or licensed counselor. In crisis or danger, please contact a
          qualified human helper.
        </Block>

        <Block icon={Cross} title="2. The AI Companion.">
          The AI ("Grace") is a software tool that generates responses using the Bible, the Ten
          Commandments, and the teachings of Jesus as its source. It can make mistakes. Test what
          it says against Scripture and a trusted believer. We are not liable for decisions made
          solely on its replies.
        </Block>

        <Block icon={Scale} title="3. Your account.">
          <ul className="list-disc list-outside ml-5 space-y-2">
            <li>You must be 13 years or older to create an account (16+ where required by local law).</li>
            <li>Keep your password private. You are responsible for what happens under your account.</li>
            <li>One person per account. You may use anonymous mode if you prefer.</li>
            <li>We may suspend accounts used to harass others, scrape data, or break the law.</li>
          </ul>
        </Block>

        <Block icon={ScrollText} title="4. What you may use the app for.">
          Personal spiritual reflection, prayer, learning, and pastoral counsel. You agree not to:
          <ul className="list-disc list-outside ml-5 space-y-2 mt-2">
            <li>Use the app to attack, dox, or threaten any person or group.</li>
            <li>Reverse-engineer, scrape, or republish other users' content.</li>
            <li>Submit content that violates the law where you live or use the service.</li>
          </ul>
        </Block>

        <Block icon={HeartHandshake} title="5. Cost.">
          Path to Repentance is free to use. We do not charge fees, run paid tiers, or display
          third-party advertising. If you ever choose to support the project, it will be entirely
          optional and clearly described.
        </Block>

        <Block icon={Scale} title="6. Privacy.">
          Your data handling is described fully on our <a href="/privacy" className="text-brand-skyDark underline">Privacy page</a>.
          The short version: everything you record is yours, encrypted in transit, scoped to
          your account, and never sold.
        </Block>

        <Block icon={ScrollText} title="7. Disclaimers and liability.">
          The app is provided "as is." We make no warranty of uninterrupted service or
          theological perfection. To the maximum extent permitted by law, Path to Repentance and its
          contributors are not liable for indirect, incidental, or consequential damages from
          your use of the service.
        </Block>

        <Block icon={Cross} title="8. Changes to these terms.">
          We may update these terms when the service evolves. Material changes will be announced
          in-app or by email. Continued use after a change means you accept the revised terms.
          If you disagree, you may close your account at any time.
        </Block>

        <Block icon={HeartHandshake} title="9. Contact.">
          Questions or concerns about these terms?
          Write to <span className="text-brand-ink font-medium">noreply@pathtorepentance.com</span>.
        </Block>

        <div className="rounded-2xl bg-white border border-brand-line p-7">
          <p className="scripture-quote text-2xl text-brand-ink leading-snug">
            “Let your 'Yes' be 'Yes' and your 'No' be 'No.'”
          </p>
          <p className="text-xs text-brand-muted mt-1">— Matthew 5:37</p>
          <p className="text-xs text-brand-muted mt-4">
            Last updated: {new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}.
          </p>
        </div>
      </section>
    </PublicLayout>
  );
}

function Block({ icon: Icon, title, children }) {
  return (
    <article className="flex gap-5">
      <span className="shrink-0 w-12 h-12 rounded-full bg-brand-mist text-brand-skyDark grid place-items-center">
        <Icon className="w-5 h-5" strokeWidth={1.6} />
      </span>
      <div className="flex-1">
        <h2 className="font-serif text-3xl text-brand-ink tracking-tight">{title}</h2>
        <div className="mt-3 text-brand-quiet leading-relaxed">{children}</div>
      </div>
    </article>
  );
}
